


























































import { Component, Mixins, Prop } from 'vue-property-decorator';
import { Action, Mutation, State } from 'vuex-class';
import { IGame, GamePlatform } from '~/store/games/types';
import { IUser } from '~/store/profile/types';
import GiveAllowance from '~/components/ModalPrompts/GiveAllowance.vue';
import GiveLockAllowance from '~/components/ModalPrompts/GiveLockAllowance.vue';
import acceptAgreementsMutation from '~/mutations/acceptAgreements.gql';
import getNeededAgreements from '~/queries/neededAgreements.gql';
import { CheckForRequiredAllowances } from '~/mixins';
import NoticeDialog from '~/components/UIComponents/NoticeDialog.vue';
import { ISanityGalaLauncher } from '~/store/cms_content/types';
import Restrictions from '~/mixins/Restrictions';

const launcherAgreementName = 'launcher_eula';
const launcherInitialVersion = '2021-09-24T17:12:40.000+00:00';

@Component({ components: { GiveAllowance, GiveLockAllowance, NoticeDialog } })
export default class GameActionButton extends Mixins(
  CheckForRequiredAllowances,
  Restrictions,
) {
  @State(profile => profile.user, { namespace: 'profile' })
  currentUser!: IUser;
  @State(profile => profile.user.hasGyriPassphrase, { namespace: 'profile' })
  hasGyriPassphrase!: boolean;
  @State(cmsContent => cmsContent.galaLauncherInfo, { namespace: 'cmsContent' })
  galaLauncherInfo!: ISanityGalaLauncher;

  @Prop(Object) readonly game!: IGame;
  @Prop({ type: String, default: 'medium-font' })
  readonly largeScreenFontSize!: IGame;

  @Prop({ type: Boolean, default: false })
  includeGameNameOnButton!: boolean;

  @Prop(Boolean) readonly lowercaseText!: boolean;

  @Action('addActiveUser', { namespace: 'games' })
  public addActiveUser!: (data: {
    gameName: string;
    cacheTtl?: number;
  }) => Promise<void>;

  @Mutation toggleErrorSnackbar!: (payload?: boolean) => void;
  @Mutation updateSnackbarErrorText!: (args: any) => void;
  @Action('promptToRegister')
  private promptToRegister!: (options?: {
    redirectAfterLoginPath?: string;
  }) => void;
  @Mutation('toggleCreateWalletPrompt')
  private toggleCreateWalletPrompt!: (payload: {
    show?: boolean;
    walletType?: string;
  }) => void;

  isDisabled: boolean = false;
  isDownloadingGame: boolean = false;
  isInstallStarted: boolean = false;
  showGiveAllowanceDialog: boolean = false;
  winIcon: any = require('@/assets/logos/win-logo.png');
  macIcon: any = require('@/assets/logos/mac-logo.png');
  displayIcon: any = null;
  isWindows: boolean = navigator.userAgent.includes('Win');
  isMac: boolean = navigator.userAgent.includes('Mac');
  downloadPercent: number = 0;
  qualified = false;

  launcherInstallerLatestVersion: string | undefined;
  launcherUpgradeFixVersion: string | undefined;
  launcherInstallerUrlWin: string | undefined;
  launcherInstallerUrlMac: string | undefined;
  launcherGameServerEndpointWin: string | undefined;
  launcherGameServerEndpointMac: string | undefined;

  launcherMessageListenerHandle: any = null;
  GameLauncherService = this.$initGameLauncherService();
  shouldShowModal: boolean = false;

  async created() {
    this.launcherInstallerLatestVersion = this.galaLauncherInfo.version;
    this.launcherUpgradeFixVersion = this.galaLauncherInfo.upgradeFixVersion;
    this.launcherInstallerUrlWin = this.galaLauncherInfo.installerUrlWin;
    this.launcherInstallerUrlMac = this.galaLauncherInfo.installerUrlMac;
    this.launcherGameServerEndpointWin = this.galaLauncherInfo.gameServerEndpointWin;
    this.launcherGameServerEndpointMac = this.galaLauncherInfo.gameServerEndpointMac;

    this.isDisabled =
      ((this.game.platform.includes(GamePlatform.Win) ||
        this.game.platform.includes(GamePlatform.Mac)) &&
        (this.GameLauncherService.isAwaitingLauncher ||
          this.GameLauncherService.isInstallingGame ||
          this.GameLauncherService.isDownloadingGame)) ||
      (this.currentUser.isInBlockedCountry &&
        this.currentUser.shouldBlockGameplay);

    this.launcherMessageListenerHandle = (event: any) => {
      if (event && event.message) {
        if (event.message.command === 'launchGame') {
          this.isDisabled = false;
        } else if (event.message.error) {
          this.isDisabled = false;
        }
      }
    };
    this.GameLauncherService.addEventListener(
      'message',
      this.launcherMessageListenerHandle,
    );

    if (this.game.hasRestrictionsToPlay && this.game.restrictionId) {
      this.qualified = await this.isQualified(this.game.restrictionId, {});
    } else {
      this.qualified = true;
    }
  }

  destroyed() {
    if (this.launcherMessageListenerHandle) {
      this.GameLauncherService.removeEventListener(
        'message',
        this.launcherMessageListenerHandle,
      );
    }
  }

  handleCloseModal() {
    const dateExpiry = new Date().valueOf() + 24 * 60 * 60 * 1000;
    localStorage.setItem('countryRestrictionPopup', dateExpiry.toString());
    this.shouldShowModal = false;
    this.initAction();
  }

  async acceptLauncherAgreement() {
    const acceptedDocuments = [
      {
        documentType: launcherAgreementName,
        versionAccepted: launcherInitialVersion,
      },
    ];

    try {
      await this.$apollo.mutate({
        mutation: acceptAgreementsMutation,
        variables: {
          acceptedDocuments,
        },
        refetchQueries: [
          {
            query: getNeededAgreements,
          },
        ],
      });

      // UA tracking
      this.$ua.trackLauncherAgreementAcceptanceCompleteEvent({
        userAgent: navigator.userAgent,
        currentVersion: this.GameLauncherService.currentVersion,
        acceptedDocuments,
      });
    } catch (err) {
      // UA tracking
      this.$ua.trackLauncherAgreementAcceptanceErrorEvent({
        userAgent: navigator.userAgent,
        currentVersion: this.GameLauncherService.currentVersion,
        acceptedDocuments,
        error: err,
      });

      console.warn(err);
      this.$sentry.captureException(err);
    }
  }

  show() {
    if (!this.qualified) {
      this.$emit('updateGameActionButtonVisiblility', false);
      return false;
    }

    let showButton = false;

    if (
      this.game.platform.includes(GamePlatform.Browser) &&
      this.game.isReleased &&
      this.game.playLink
    ) {
      showButton = true;
    } else if (
      (this.game.platform.includes(GamePlatform.Win) ||
        this.game.platform.includes(GamePlatform.Mac)) &&
      this.game.isReleased
    ) {
      if (
        (this.isWindows && this.game.isWinReleased) ||
        (this.isMac && this.game.isMacReleased)
      ) {
        showButton = true;
      }
    }

    this.$emit('updateGameActionButtonVisiblility', showButton);

    return showButton;
  }

  getGameLatestVersion() {
    if (this.isWindows) {
      return this.game.latestWindowsVersion;
    }

    if (this.isMac) {
      return this.game.latestMacVersion;
    }

    return undefined;
  }

  getGameUsesCreators() {
    if (this.isWindows) {
      return this.game.windowsUsesCreators;
    }

    if (this.isMac) {
      return this.game.macUsesCreators;
    }
  }

  async doAction() {
    if (!this.qualified) return;
    try {
      this.showGiveAllowanceDialog = false;
      this.isDisabled = true;

      if (this.game.platform.includes(GamePlatform.Browser)) {
        if (process.env.displayGameInIframe) {
          if (document.body.requestFullscreen) {
            document.body.requestFullscreen();
          } else if ((document.body as any).webkitRequestFullscreen) {
            (document.body as any).webkitRequestFullscreen();
          }
        }

        // UA tracking
        this.$ua.trackGamePlayBeginEvent({
          userAgent: navigator.userAgent,
          game: this.game?.codeName,
          platform: GamePlatform.Browser,
          isWindows: this.isWindows,
          isMac: this.isMac,
        });

        if (this.game?.codeName && !this.game.activePlayerCountInDataBricks) {
          this.addActiveUser({
            gameName: this.game.codeName,
            cacheTtl: this.game.timePlayerIsActive,
          });
        }

        this.$router.push(`/games/${this.game.route}/play`);
      } else if (
        this.game.platform.includes(GamePlatform.Win) ||
        this.game.platform.includes(GamePlatform.Mac)
      ) {
        if (this.GameLauncherService.socket.readyState === WebSocket.OPEN) {
          // Socket open (Launcher is installed)

          this.acceptLauncherAgreement();

          let installerUrl = null;

          if (this.isWindows && this.game.isWinReleased) {
            // Install game, windows version
            installerUrl = this.game.installerUrlWin;
          } else if (this.isMac && this.game.isMacReleased) {
            // Install game, mac version
            installerUrl = this.game.installerUrlMac;
          } else {
            throw new Error('Platform not supported');
          }

          if (
            this.launcherInstallerLatestVersion &&
            this.GameLauncherService.currentVersion &&
            this.launcherInstallerLatestVersion !==
              this.GameLauncherService.currentVersion
          ) {
            // Update Launcher
            let launcherInstallerUrl: string;
            if (!this.launcherUpgradeFixVersion) {
              throw new Error('Missing upgrade fix version');
            } else if (
              this.isWindows &&
              this.launcherInstallerUrlWin &&
              this.launcherGameServerEndpointWin
            ) {
              // Download launcher, windows version
              launcherInstallerUrl =
                this.versionCompare(
                  this.GameLauncherService.currentVersion,
                  this.launcherUpgradeFixVersion,
                ) > -1
                  ? `${this.launcherGameServerEndpointWin}/${this.launcherInstallerLatestVersion}`
                  : this.launcherInstallerUrlWin;
            } else if (
              this.isMac &&
              this.launcherInstallerUrlMac &&
              this.launcherGameServerEndpointMac
            ) {
              // Download launcher, mac version
              launcherInstallerUrl =
                this.versionCompare(
                  this.GameLauncherService.currentVersion,
                  this.launcherUpgradeFixVersion,
                ) > -1
                  ? `${this.launcherGameServerEndpointMac}/${this.launcherInstallerLatestVersion}`
                  : this.launcherInstallerUrlMac;
            } else {
              throw new Error('Platform not supported');
            }

            this.isDownloadingGame = true;
            const resp = await this.GameLauncherService.updateLauncher({
              command: 'updateLauncher',
              params: {
                url: launcherInstallerUrl,
                jwt: this.currentUser.gameToken,
              },
            });
            this.isDownloadingGame = false;

            if (resp.type === 'complete') {
              this.GameLauncherService.autoReconnect = true;
            }
          } else if (
            !this.game.isInstalled &&
            installerUrl &&
            this.getGameLatestVersion()
          ) {
            // Install Game
            const resp = await this.GameLauncherService.installGame(
              {
                command: 'installGame',
                params: {
                  gameName: this.game.codeName,
                  url: `${installerUrl}/${this.getGameLatestVersion()}`,
                  jwt: this.currentUser.gameToken,
                  useCreators: this.getGameUsesCreators(),
                },
              },
              percent => {},
            );
          } else if (
            this.getGameLatestVersion() &&
            this.game.installedVersion &&
            this.getGameLatestVersion() !== this.game.installedVersion
          ) {
            // Update Game
            const resp = await this.GameLauncherService.updateGame(
              {
                command: 'updateGame',
                params: {
                  gameName: this.game.codeName,
                  url: `${installerUrl}/${this.getGameLatestVersion()}`,
                  jwt: this.currentUser.gameToken,
                  toVersion: this.getGameLatestVersion(),
                  useCreators: this.getGameUsesCreators(),
                },
              },
              percent => {},
            );
          } else if (this.game.isInstalled) {
            // UA tracking
            this.$ua.trackGamePlayBeginEvent({
              userAgent: navigator.userAgent,
              game: this.game?.codeName,
              isWindows: this.isWindows,
              isMac: this.isMac,
            });

            // Game is installed, Launch it
            const resp = await this.GameLauncherService.send({
              command: 'launchGame',
              params: {
                gameName: this.game.codeName,
                jwt: this.currentUser.gameToken,
              },
            });
          }
        } else if (
          this.GameLauncherService.socket.readyState !== WebSocket.OPEN
        ) {
          // Socket closed (Launcher is NOT installed)
          let launcherInstallerUrl: string;
          if (this.isWindows && this.launcherInstallerUrlWin) {
            // Download launcher, windows version
            launcherInstallerUrl = this.launcherInstallerUrlWin;
          } else if (this.isMac && this.launcherInstallerUrlMac) {
            // Download launcher, mac version
            launcherInstallerUrl = this.launcherInstallerUrlMac;
          } else {
            throw new Error('Platform not supported');
          }

          // UA tracking
          this.$ua.trackLauncherInitialDownloadBeginEvent({
            userAgent: navigator.userAgent,
            currentVersion: this.GameLauncherService.currentVersion,
            isWindows: this.isWindows,
            isMac: this.isMac,
          });

          const link = document.createElement('a');
          link.href = launcherInstallerUrl;
          document.body.appendChild(link);
          link.click();
          link.remove();

          this.GameLauncherService.autoReconnect = true;
          this.GameLauncherService.connect();

          this.isDisabled = false;
        }
      }
    } catch (err) {
      // UA tracking
      this.$ua.trackGameActionErrorEvent({
        userAgent: navigator.userAgent,
        game: this.game?.codeName,
        error: err,
        isWindows: this.isWindows,
        isMac: this.isMac,
      });
    }
  }

  async initAction() {
    const passedAuthValidation = await this.validateAuthRequirement();
    if (!passedAuthValidation) {
      // UA tracking
      this.$ua.trackGameActionErrorEvent({
        userAgent: navigator.userAgent,
        game: this.game?.codeName,
        error: 'FAILED_AUTH_VALIDATION',
        isWindows: this.isWindows,
        isMac: this.isMac,
      });

      return;
    }

    if (
      this.currentUser.isInBlockedCountry &&
      this.currentUser.shouldBlockGameplay
    ) {
      // UA tracking
      this.$ua.trackGameActionErrorEvent({
        userAgent: navigator.userAgent,
        game: this.game?.codeName,
        error: 'USER_IS_IN_BLOCKED_COUNTRY',
        isWindows: this.isWindows,
        isMac: this.isMac,
      });

      return;
    }

    const dateExpiry = localStorage.getItem(
      'countryRestrictionPopup',
    ) as string;
    // This checks if a user already saw the popup within the last 24 hours
    if ((dateExpiry && new Date(dateExpiry) < new Date()) || !dateExpiry) {
      if (
        this.currentUser.isInBlockedCountry &&
        !this.currentUser.shouldBlockGameplay
      ) {
        return (this.shouldShowModal = true);
      }
    }

    try {
      this.isDisabled = true;
      const requiredAllowances = await this.checkAllowanceRequirement(
        this.game,
      );

      const needsLockAllowance = requiredAllowances.needsLockAllowance;

      if (needsLockAllowance) {
        if (this.hasGyriPassphrase) {
          this.showGiveAllowanceDialog = true;
        } else {
          this.toggleCreateWalletPrompt({ show: true, walletType: 'gyri' });
          this.isDisabled = false;
        }
      } else {
        this.doAction();
      }
    } catch (error) {
      // UA tracking
      this.$ua.trackGameActionErrorEvent({
        userAgent: navigator.userAgent,
        game: this.game?.codeName,
        error,
        isWindows: this.isWindows,
        isMac: this.isMac,
      });

      console.error(error);
      this.isDisabled = false;
      this.showErrorSnackbar();
    }
  }

  cancel() {
    this.showGiveAllowanceDialog = false;
    this.isDisabled = false;
  }

  showErrorSnackbar(message = 'Something went wrong.') {
    this.updateSnackbarErrorText(message);
    this.toggleErrorSnackbar(true);
  }

  playReadyButtonText() {
    return this.includeGameNameOnButton
      ? (this.$t('components.games.gameActionButton.playGame', {
          gameName: this.game.displayName,
        }) as string)
      : (this.$t('components.games.gameActionButton.play') as string);
  }

  buttonText() {
    let text = 'Button text';
    if (this.game.platform.includes(GamePlatform.Browser)) {
      text = this.playReadyButtonText();
      this.displayIcon = null;
    } else if (
      this.game.platform.includes(GamePlatform.Win) ||
      this.game.platform.includes(GamePlatform.Mac)
    ) {
      if (this.GameLauncherService.isAwaitingLauncher) {
        text = this.$t(
          'components.games.gameActionButton.awaitingLauncher',
        ) as string;
        this.displayIcon = null;
      } else if (
        this.GameLauncherService.socket.readyState === WebSocket.OPEN
      ) {
        if (this.isDownloadingGame) {
          text = this.$t('components.games.gameActionButton.downloading', {
            percent: this.downloadPercent,
          }) as string;
        } else if (this.GameLauncherService.isDownloadingGame) {
          if (
            this.GameLauncherService.gameNameInProgress ===
              this.game.codeName ||
            this.GameLauncherService.gameNameInProgress === 'gala-launcher'
          ) {
            text = this.$t('components.games.gameActionButton.downloading', {
              percent: this.GameLauncherService.downloadPercent,
            }) as string;
          } else {
            text = 'Launcher Busy';
          }
        } else if (this.isInstallStarted) {
          text = this.$t(
            'components.games.gameActionButton.installing',
          ) as string;
        } else if (this.GameLauncherService.isInstallingGame) {
          if (
            this.GameLauncherService.gameNameInProgress ===
              this.game.codeName ||
            this.GameLauncherService.gameNameInProgress === 'gala-launcher'
          ) {
            text = this.$t(
              'components.games.gameActionButton.installing',
            ) as string;
          } else {
            text = 'Launcher Busy';
          }
        } else if (
          this.launcherInstallerLatestVersion &&
          this.GameLauncherService.currentVersion &&
          this.launcherInstallerLatestVersion !==
            this.GameLauncherService.currentVersion
        ) {
          text = this.$t(
            'components.games.gameActionButton.updateLauncher',
          ) as string;
        } else if (
          this.getGameLatestVersion() &&
          this.game.installedVersion &&
          this.getGameLatestVersion() !== this.game.installedVersion
        ) {
          text = this.$t(
            'components.games.gameActionButton.updateGame',
          ) as string;
        } else if (this.game.isInstalled) {
          text = this.playReadyButtonText();
          this.displayIcon = null;
        } else {
          text = this.$t(
            'components.games.gameActionButton.installAndPlay',
          ) as string;
        }

        this.displayIcon = null;
      } else if (this.isWindows) {
        text = this.$t(
          'components.games.gameActionButton.downloadLauncher',
        ) as string;
        this.displayIcon = this.winIcon;
      } else if (this.isMac) {
        text = this.$t(
          'components.games.gameActionButton.downloadLauncher',
        ) as string;
        this.displayIcon = this.macIcon;
      }
    }

    return text;
  }

  async validateAuthRequirement() {
    if (this.game.requireAuthToPlay && !(await this.$auth.isLoggedIn())) {
      if (this.currentUser.loggedIn) {
        window.location.assign(
          `${window.location.origin}${this.$route.path}?register=1`,
        );
      } else {
        this.promptToRegister({ redirectAfterLoginPath: this.$route.fullPath });
      }
      return false;
    }
    return true;
  }

  versionCompare(
    v1: any,
    v2: any,
    options?: { lexicographical: boolean; zeroExtend: boolean },
  ) {
    let lexicographical = options && options.lexicographical,
      zeroExtend = options && options.zeroExtend,
      v1parts = v1.split('.'),
      v2parts = v2.split('.');

    const isValidPart = (x: any) => {
      return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x);
    };

    if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
      return NaN;
    }

    if (zeroExtend) {
      while (v1parts.length < v2parts.length) v1parts.push('0');
      while (v2parts.length < v1parts.length) v2parts.push('0');
    }

    if (!lexicographical) {
      v1parts = v1parts.map(Number);
      v2parts = v2parts.map(Number);
    }

    for (var i = 0; i < v1parts.length; ++i) {
      if (v2parts.length == i) {
        return 1;
      }

      if (v1parts[i] == v2parts[i]) {
        continue;
      } else if (v1parts[i] > v2parts[i]) {
        return 1;
      } else {
        return -1;
      }
    }

    if (v1parts.length != v2parts.length) {
      return -1;
    }

    return 0;
  }
}
